import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { CONSENT_COPY, PRIVACY_HREF } from './consentCopy';

/**
 * The cookie/tracking consent banner shown to California and Florida
 * visitors before GA4 and the Meta Pixel are allowed to fire.
 *
 * Presentational only — it renders and calls back. The host
 * (`ConsentBannerHost`) owns when it appears and what a click means.
 *
 * ## Accessibility decisions, and why
 *
 * **Non-modal.** `role="region"` + `aria-label`, NOT `role="dialog"
 * aria-modal="true"`. No scrim, no focus trap, no scroll lock, no stolen
 * focus on mount. Three reasons:
 *   - the US state privacy regimes this serves (CCPA/CPRA and the newer
 *     comprehensive state laws) do not require a blocking wall, so trapping
 *     the page would be a self-inflicted a11y regression for zero legal
 *     benefit;
 *   - moving focus on page load is itself a WCAG problem (3.2.1 On Focus) and
 *     yanks a sighted keyboard user out of whatever they were doing;
 *   - the brief is explicit that the banner must not trap focus or block the
 *     page. That matters more now that it is global: it is on every visitor's
 *     first page view.
 *
 * **Portalled to the FRONT of `<body>`.** The tradeoff of not stealing focus
 * is that a keyboard user could have to tab the whole page to reach the
 * banner. Mounting it as body's *first* child puts it first in tab order, so
 * the first Tab press lands on Accept. This is the reason the portal target is
 * hand-built rather than a plain `createPortal(…, document.body)` append.
 *
 * **Equal-weight buttons.** Accept and Decline get identical geometry and
 * hit area; only the fill differs. A prominent Accept next to a faded
 * text-link Decline is the exact dark pattern the CPRA regulations name
 * (symmetry-in-choice, §7004(a)(2)), so `ui/Button`'s `ghost` variant is
 * deliberately not used for Decline.
 */

/**
 * Above every PERSISTENT bottom bar — the quiz's sticky Continue (50), the
 * landing CTA (50), the mobile tab bar (50), the guide quiz bar (30) — but
 * BELOW the transient sheet/overlay tier (60: SavePickerSheet, AboutSheet,
 * BoardSearchPill; 70: EmailCaptureSheet; 80: toasts) and far below
 * `ui/Modal` (120) / `ui/Drawer` (200/210).
 *
 * The banner OVERLAYS persistent bars; it does not displace them (Kate,
 * 2026-09-04). An earlier build measured the banner and pushed every
 * bottom-pinned bar up by its height via a `--thea-consent-inset` custom
 * property. That was worse than the problem: on mobile it shoved the tab bar
 * into the middle of the screen, which read as a layout bug rather than as a
 * notice. Floating over is what a consent notice is supposed to do, and the
 * banner is dismissable precisely so a covered control is one tap away.
 *
 * Transient sheets still SHOULD win over the banner. A modal is a task the
 * user opened; covering its submit button with a consent notice is a real
 * defect. 55 sits above the persistent bars (50) and below the sheet tier
 * (60+), so the sheet covers the banner and the banner is still there when
 * the sheet closes.
 */
const BANNER_Z = 55;

const Region = styled.section`
  position: fixed;
  z-index: ${BANNER_Z};
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  /* Clear two things: the iOS home indicator / Android gesture bar, and any
     bottom-pinned bar that is currently on screen.

     The BANNER moves, the bars never do — see bottomBarInset.ts for why that
     direction is the whole point. When no blocking bar is mounted the variable
     is 0px and this is just the safe-area padding. */
  padding: 10px 10px
    calc(
      10px + env(safe-area-inset-bottom, 0px) +
        var(--thea-bottom-bar-inset, 0px)
    );
  pointer-events: none;
  /* The lift is animated so a bar appearing mid-session (the quiz CTA fading
     in) slides the banner up rather than teleporting it. */
  transition: padding-bottom 180ms var(--ease-settle);
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* Desktop: a card in the bottom-LEFT corner rather than a centred bar
     (Kate, 2026-09-02). Centred, it read as a full-width interruption sitting
     on top of the page's own centred CTAs. Anchored left it stays out of the
     way of everything that centres itself. Below 640px it is effectively
     full-width anyway, so the anchor makes no difference there. */
  @media (min-width: 640px) {
    justify-content: flex-start;
    padding-left: 20px;
    /* Same rule on desktop: the sticky footer keeps its place, the banner
       rides above it. */
    padding-bottom: calc(20px + var(--thea-bottom-bar-inset, 0px));
  }
`;

const Card = styled.div<{ $drag: number; $dragging: boolean }>`
  pointer-events: auto;
  touch-action: pan-y;
  position: relative;
  width: 100%;
  /* Was 560px. A consent notice does not need half the screen, and the width
     is what forced the copy into a tall block. */
  max-width: 420px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Extra right padding clears the absolutely-positioned dismiss button so the
     copy never runs underneath it. */
  padding: 13px 15px 13px 15px;
  background: ${({ theme }) => theme.color.creamLight};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: ${({ theme }) => theme.radius['2xl']};
  box-shadow: ${({ theme }) => theme.shadow.xl};
  font-family: ${({ theme }) => theme.font.sans};

  /* While the finger is down the card tracks it 1:1 and the entry animation is
     off — otherwise the keyframe's final "transform: none" would fight the
     drag. On release the dragging flag goes false and the transition animates
     the card back to rest. */
  transform: ${({ $drag }) => ($drag ? `translateY(${$drag}px)` : 'none')};
  transition: ${({ $dragging }) => ($dragging ? 'none' : 'transform 200ms var(--ease-settle)')};

  animation: ${({ $drag }) => ($drag ? 'none' : 'consentBannerIn 220ms var(--ease-settle) both')};
  @keyframes consentBannerIn {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

`;

/**
 * The visible <h2> was removed to cut height (Kate: the banner was 295px on a
 * 390px-wide phone — 35% of the viewport). Height still matters now that the
 * banner floats over the bottom bars rather than moving them: every pixel of
 * banner is a pixel of app it covers until the visitor answers or dismisses.
 *
 * NOT an accessibility regression: the region still carries an accessible name
 * via `aria-label={CONSENT_COPY.regionLabel}`, which is what a screen reader
 * announces on entry. The heading was duplicating that name visually. If the
 * incoming copy spec wants a visible title back, restore it here — the string
 * is already in `consentCopy`.
 */
const Body = styled.p`
  margin: 0;
  padding-right: 26px; /* clears the dismiss button */
  font-size: 13.5px;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.mutedText};

  a {
    color: ${({ theme }) => theme.color.clay};
    text-decoration: underline;
    /* 2px so the underline survives the descenders at 14px. */
    text-underline-offset: 2px;
  }
`;

/**
 * Buttons sit side by side at EVERY width now, including mobile. Stacking them
 * cost ~54px of height on phones, which is where the height mattered most, and
 * "Decline" / "Accept" are short enough to sit comfortably in one row even at
 * 320px.
 */
const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
`;

/** Shared geometry — the symmetry that keeps this out of dark-pattern territory. */
const ChoiceButton = styled.button`
  flex: 1 1 0;
  /* 40px: comfortably past WCAG 2.5.8 (AA, 24x24) while trimming height. */
  min-height: 40px;
  padding: 9px 16px;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 140ms ease, box-shadow 140ms ease;

  &:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
  }

  @media (min-width: 640px) {
    flex: 0 0 auto;
    min-width: 108px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const DeclineButton = styled(ChoiceButton)`
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: ${({ theme }) => theme.color.neutralButton};
  color: ${({ theme }) => theme.color.inkSoft};

  &:hover {
    background: ${({ theme }) => theme.color.neutralButtonPressed};
  }
`;

const AcceptButton = styled(ChoiceButton)`
  border: 1px solid transparent;
  background: ${({ theme }) => theme.gradient.cta};
  color: #fff;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow.soft};
  }
`;

/**
 * The X. "Not now" — explicitly NOT a third answer to the consent question.
 *
 * Visually de-emphasised relative to Accept/Decline on purpose, and that is
 * the correct direction here: the two real choices stay equal to each other
 * (CPRA §7004(a)(2) symmetry), while the escape hatch reads as an escape
 * hatch. It cannot be a dark pattern because it grants nothing — see
 * `dismissConsentBanner` for what it does and does not mean.
 */
const DismissButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  /* 34px square — past WCAG 2.5.8 (AA, 24x24) without adding card height,
     since it is absolutely positioned and out of flow. */
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  color: ${({ theme }) => theme.color.mutedText};
  font-size: 17px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.neutralButton};
  }
  &:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
  }
`;

/**
 * Distance the card must travel before release counts as "swipe it away".
 *
 * 56px is far enough that a scroll that begins on the card, or a tap with a
 * little drift, does not dismiss by accident — the banner grants nothing, but
 * a notice that vanishes on a stray touch is one the visitor never got to
 * read.
 */
const SWIPE_DISMISS_PX = 56;

/**
 * Swipe-down-to-dismiss for touch (Kate, 2026-09-04: "swipe it away on
 * mobile").
 *
 * DOWN only, and never up: down is the direction that matches where the card
 * lives and where it leaves to, and it is the gesture a bottom sheet has
 * trained everyone to expect. Upward drag is clamped to 0 so the card cannot
 * be flung over the page content it is supposed to sit beside.
 *
 * The card is `touch-action: pan-y`, so the browser still owns vertical
 * scrolling; we never call `preventDefault`. A visitor who starts a scroll on
 * the banner scrolls the page, and only a deliberate downward pull past the
 * threshold dismisses. That is why the threshold is generous rather than tight.
 *
 * Dismiss means exactly what the X means — "not now", nothing granted. See
 * `dismissConsentBanner`.
 */
function useSwipeToDismiss(onDismiss: () => void) {
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startY = useRef<number | null>(null);

  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch) return;
    startY.current = touch.clientY;
    setDragging(true);
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    if (!touch || startY.current === null) return;
    // Clamp upward movement: this card only ever leaves downward.
    setDrag(Math.max(0, touch.clientY - startY.current));
  };

  const end = () => {
    if (startY.current === null) return;
    const travelled = drag;
    startY.current = null;
    setDragging(false);
    if (travelled >= SWIPE_DISMISS_PX) {
      onDismiss();
      return;
    }
    setDrag(0); // Snap back — the transition above animates it.
  };

  return {
    drag,
    dragging,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: end,
      onTouchCancel: end,
    },
  };
}

export interface ConsentBannerProps {
  onAccept: () => void;
  onDecline: () => void;
  /** "Not now": hides the banner for this session and grants nothing. */
  onDismiss: () => void;
}

/**
 * Build (once) a mount node pinned to the front of `<body>` — see the tab-order
 * note in the docblock. Idempotent across remounts within a page load.
 */
function useFrontOfBodyPortalNode(): HTMLElement | null {
  const nodeRef = useRef<HTMLElement | null>(null);
  const node = useMemo(() => {
    if (typeof document === 'undefined') return null;
    const existing = document.getElementById('thea-consent-root');
    if (existing) return existing;
    const el = document.createElement('div');
    el.id = 'thea-consent-root';
    document.body.insertBefore(el, document.body.firstChild);
    return el;
  }, []);
  nodeRef.current = node;

  useEffect(
    () => () => {
      const el = nodeRef.current;
      // Only remove it if React has already emptied it, so a concurrent
      // StrictMode double-mount can't tear out a live tree.
      if (el && el.parentNode && el.childNodes.length === 0) {
        el.parentNode.removeChild(el);
      }
    },
    [],
  );

  return node;
}

export function ConsentBanner({
  onAccept,
  onDecline,
  onDismiss,
}: ConsentBannerProps): React.ReactElement | null {
  const portalNode = useFrontOfBodyPortalNode();
  const regionRef = useRef<HTMLElement>(null);
  const swipe = useSwipeToDismiss(onDismiss);
  if (!portalNode) return null;

  return createPortal(
    <Region
      ref={regionRef}
      // `region` + a name, not `dialog` — this does not trap or block.
      role="region"
      aria-label={CONSENT_COPY.regionLabel}
      aria-describedby="thea-consent-description"
      data-testid="consent-banner"
    >
      <Card
        $drag={swipe.drag}
        $dragging={swipe.dragging}
        data-testid="consent-card"
        {...swipe.handlers}
      >
        <DismissButton
          type="button"
          onClick={onDismiss}
          aria-label={CONSENT_COPY.dismiss}
          data-testid="consent-dismiss"
        >
          {/* aria-hidden: the button's accessible name comes from aria-label,
              so the glyph must not be announced as "multiplication sign". */}
          <span aria-hidden="true">&times;</span>
        </DismissButton>
        <Body id="thea-consent-description">
          {CONSENT_COPY.bodyLead}
          <a href={PRIVACY_HREF}>{CONSENT_COPY.privacyLinkText}</a>
          {CONSENT_COPY.bodyTail}
        </Body>
        <Actions>
          <DeclineButton type="button" onClick={onDecline}>
            {CONSENT_COPY.decline}
          </DeclineButton>
          <AcceptButton type="button" onClick={onAccept}>
            {CONSENT_COPY.accept}
          </AcceptButton>
        </Actions>
      </Card>
    </Region>,
    portalNode,
  );
}
